import { dxfDrawing, sheetsOf, type DxfGeo } from './dxfImport';
import type { PdfPageData, PdfPath, PdfText } from './pdfExtract';
import { pdfToPages, type PdfImportResult } from './pdfImport';
import type { Pt } from './types';

/*
 * DXF schematics → EnerCAD projects, through the same conversion as EPLAN
 * PDFs: each sheet (a frame of paper proportions, A4 … A0, in model space)
 * becomes a page in millimetres with y down, its geometry keeping colour and
 * fill, its texts their size, direction and weight; then wires, interruption
 * points, devices and terminals are read exactly as from a PDF.
 */

type Box = [number, number, number, number];

/** Closed axis-aligned rectangles (corners may be rounded) of paper proportions (√2, landscape or portrait), at least A5 sized. */
function frames(geo: DxfGeo[], mm: number): Box[] {
  const out: Box[] = [];
  for (const g of geo) {
    if (g.k !== 'line' || g.fill || !(g.closed || (g.pts.length === 5 && Math.hypot(g.pts[0][0] - g.pts[4][0], g.pts[0][1] - g.pts[4][1]) < 1e-6))) continue;
    const xs = g.pts.map((p) => p[0]), ys = g.pts.map((p) => p[1]);
    const b: Box = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    const w = (b[2] - b[0]) * mm, h = (b[3] - b[1]) * mm, r = Math.max(w, h) / Math.min(w, h);
    // every point on (or, at rounded corners, near) the outline: a rectangle, not a rotated or irregular shape
    const m = 0.03 * Math.min(b[2] - b[0], b[3] - b[1]);
    if (g.pts.some((p) => Math.min(Math.abs(p[0] - b[0]), Math.abs(p[0] - b[2])) > m && Math.min(Math.abs(p[1] - b[1]), Math.abs(p[1] - b[3])) > m)) continue;
    if (Math.abs(r - Math.SQRT2) < 0.03 && Math.min(w, h) >= 140) out.push(b);
  }
  // inner frames (border inside the paper edge) give way to the outer one
  const inside = (a: Box, b: Box) => a !== b && a[0] >= b[0] - 1e-6 && a[1] >= b[1] - 1e-6 && a[2] <= b[2] + 1e-6 && a[3] <= b[3] + 1e-6;
  const sheets = out.filter((a) => !out.some((b) => inside(a, b)));
  // rows from the top (y up in DXF), then left to right
  const h = Math.max(1, ...sheets.map((s) => s[3] - s[1]));
  return sheets.sort((a, b) => Math.round((b[3] - a[3]) / (h / 2)) || a[0] - b[0]);
}

/** A circle as a polyline. */
const ring = (c: Pt, r: number): Pt[] => Array.from({ length: 25 }, (_, i) => [c[0] + r * Math.cos((i * Math.PI) / 12), c[1] + r * Math.sin((i * Math.PI) / 12)] as Pt);

/** Model space DXF → one page per sheet, in the form the PDF conversion reads. */
export function dxfToPdfPages(text: string): { pages: PdfPageData[]; warnings: string[] } {
  const { geo, warnings, mm } = dxfDrawing(text);
  let boxes = frames(geo, mm);
  // no frame of paper proportions: the separate drawings laid out side by side are the sheets
  if (!boxes.length) boxes = sheetsOf(geo).map((s) => s.bb);
  if (!boxes.length) return { pages: [], warnings };
  const centre = (g: DxfGeo): Pt => (g.k === 'line' ? [(Math.min(...g.pts.map((p) => p[0])) + Math.max(...g.pts.map((p) => p[0]))) / 2, (Math.min(...g.pts.map((p) => p[1])) + Math.max(...g.pts.map((p) => p[1]))) / 2] : g.k === 'circle' ? g.c : g.p);
  const pages: PdfPageData[] = boxes.map(([x0, y0, x1, y1]) => ({ w: (x1 - x0) * mm, h: (y1 - y0) * mm, title: '', paths: [], texts: [] }));
  for (const g of geo) {
    const c = centre(g);
    const at = boxes.findIndex((b) => c[0] >= b[0] && c[0] <= b[2] && c[1] >= b[1] && c[1] <= b[3]);
    if (at < 0) continue;
    const [x0, , , y1] = boxes[at], page = pages[at];
    // DXF y points up; the page's y down, in mm
    const T = (p: Pt): Pt => [(p[0] - x0) * mm, (y1 - p[1]) * mm];
    const col = g.col ?? '#000000';
    if (g.k === 'line') {
      const path: PdfPath = { ...(g.fill ? { fill: col } : { stroke: col }), width: 0.25, ...(g.dash ? { dash: true } : {}), ...(g.blk ? { blk: g.blk } : {}), polys: [{ pts: g.pts.map(T), closed: !!g.closed }] };
      if (g.closed && !g.fill) path.polys[0].pts.push(path.polys[0].pts[0]);
      page.paths.push(path);
    } else if (g.k === 'circle') page.paths.push({ stroke: col, width: 0.25, ...(g.blk ? { blk: g.blk } : {}), polys: [{ pts: ring(g.c, g.r).map(T), closed: true }] });
    else {
      // cap height → font size; an average glyph is about half the size wide
      const size = g.h * mm * 1.38, w = g.text.length * size * 0.5;
      const a = (g.rot * Math.PI) / 180, dir: Pt = [Math.cos(a), Math.sin(a)], up: Pt = [-Math.sin(a), Math.cos(a)];
      // from the alignment point back to the start of the baseline (DXF units, y up)
      const along = g.ha === 1 || g.ha === 4 ? w / 2 : g.ha === 2 ? w : 0, rise = g.ha === 4 || g.va === 2 ? (g.h * mm) / 2 : g.va === 3 ? g.h * mm : 0;
      const start: Pt = [g.p[0] - (dir[0] * along + up[0] * rise) / mm, g.p[1] - (dir[1] * along + up[1] * rise) / mm];
      const t: PdfText = { s: g.text, ...((([x, y]) => ({ x, y }))(T(start))), size, angle: -g.rot, w, font: g.bold ? 'Bold' : 'Regular', bold: !!g.bold, colour: col };
      page.texts.push(t);
    }
  }
  // page titles from the title block: the text under "SAYFA AÇIKLAMASI" / "PAGE DESCRIPTION" / "Seitenbeschreibung"
  for (const page of pages) {
    // the title block label ("Sayfa açıklaması", or just "Açıklama" in the bottom fifth of the sheet)
    const label =
      page.texts.find((t) => /^(sayfa a[çc][ıi]klamas[ıi]|page description|seitenbeschreibung)\s*:?$/i.test(t.s.trim())) ??
      page.texts.find((t) => /^(a[çc][ıi]klama|description|beschreibung)\s*:?$/i.test(t.s.trim()) && t.y > page.h * 0.8);
    if (!label) continue;
    const under = page.texts
      .filter((t) => t !== label && Math.abs(t.angle) < 1 && t.x > label.x - 15 && t.x < label.x + 45 && t.y > label.y + 1 && t.y < label.y + 14 && !/numara|number|nummer/i.test(t.s))
      .sort((a, b) => a.y - b.y || a.x - b.x);
    page.title = under.map((t) => t.s).join(' ').slice(0, 120);
  }
  // the old title block: texts in the band of its labels (date, drawn, checked, sheet, description …) are left out;
  // EnerCAD draws its own
  const LABEL = /^(tar[iİı]h|ç[iİı]zen|kontrol|onay|sayfa|a[çc][ıi]klama|proje( ad[ıi])?|f[iİı]rma|m[üu]şter[iİı]|[iİı]s[iİı]m|date|drawn|checked|approved|sheet|page|project|customer|datum|bearb|gepr)/i;
  for (const page of pages) {
    const labels = page.texts.filter((t) => t.y > page.h * 0.75 && LABEL.test(t.s.trim()));
    if (labels.length < 3) continue;
    const top = Math.min(...labels.map((t) => t.y - t.size)) - 2;
    page.texts = page.texts.filter((t) => t.y < top);
  }
  return { pages: pages.filter((p) => p.paths.length || p.texts.length), warnings };
}

/** Converts a DXF schematic into EnerCAD pages (see pdfToPages). */
export function dxfToProject(text: string): PdfImportResult {
  const { pages, warnings } = dxfToPdfPages(text);
  const r = pdfToPages(pages);
  return { ...r, warnings: [...warnings, ...r.warnings] };
}
